import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import axioInstance from "../../../../../../../../../custom_utilities/AxiosInstance/axiosInstance";
import { url_billing_address } from "../../../../../../../../../custom_utilities/api_services";
import { STORE_PRICE_TOAST_CONTAINER_ID } from "../../../../../../../../../custom_utilities/toastify_config";
import PaytmPayment from "../../../../../../../../../custom_utilities/paytm_payment/PaytmPayment";
import { setOrderId } from "../../../../../../../../../redux/payment_gateway/action";
import { trackWebEngageViewCoupons } from "../../../../../../../../../custom_utilities/webengage/webengageService";
import backIcon from "../../../../../../../../../utilities/images/package_course_header/icon-back.svg";
import couponIcon from "../../../../../../../../../utilities/images/package_checkout/icon-coupon.svg";
import chevronIcon from "../../../../../../../../../utilities/images/package_checkout/icon-chevron.svg";
import dividerIcon from "../../../../../../../../../utilities/images/package_checkout/icon-divider.svg";
import CouponList from "../../../../modal/Coupons/CouponList";
import { applyCoupon } from "../StoreUtils/StoreApiUtils";
import "./StoreOverview.scss";

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    return text && text !== "0" && text !== "null" ? text : "";
  }
  if (Array.isArray(value)) return value.length === 1 ? textOf(value[0]) : "";
  if (typeof value === "object") {
    return textOf(
      value.city_name ||
        value.city ||
        value.district_name ||
        value.district ||
        value.name ||
        value.title ||
        value.label ||
        value.value
    );
  }
  return "";
};

const formatRupee = (value) => {
  const amount = Math.round(Number(value) || 0);
  return `₹${Math.abs(amount).toLocaleString("en-IN")}`;
};

const durationChip = (details = {}) => {
  const named = `${details.package_name || ""} ${textOf(details.duration) || ""}`.toLowerCase();
  if (/6\s*months?/.test(named)) return "6 months";
  if (/2\s*years?/.test(named)) return "2 Years";
  if (/1\s*year|12\s*months?/.test(named)) return "1 Year";
  const months = Number(details.duration_months || details.validity_months);
  if (months === 6) return "6 months";
  if (months === 12) return "1 Year";
  if (months === 24) return "2 Years";
  return textOf(details.validity || details.package_duration);
};

const examFromName = (name = "") => {
  const match = String(name).match(/\b(JEE|NEET|BITSAT)\b/i);
  return match ? match[1].toUpperCase() : "";
};

const productChips = (details = {}) => {
  const chips = [
    textOf(
      details.target_name ||
        details.target ||
        details.exam_name ||
        details.exam ||
        details.course_name ||
        details.course
    ) || examFromName(details.package_name),
    textOf(
      details.class_name || details.class || details.student_class || details.class_title
    ),
    textOf(
      details.medium_name || details.medium || details.language_name || details.language
    ),
    durationChip(details),
  ];
  return chips.filter((chip, index) => chip && chips.indexOf(chip) === index);
};

const STATE_KEYS = ["state_name", "billing_state", "user_state", "state"];
const CITY_KEYS = [
  "city_name",
  "billing_city",
  "billing_district",
  "district_name",
  "user_city",
  "user_district",
  "town",
  "district",
  "city",
];

const readNamedField = (node, keys, depth = 0) => {
  if (!node || depth > 6) return "";
  if (Array.isArray(node)) {
    return node.length === 1 ? readNamedField(node[0], keys, depth + 1) : "";
  }
  if (typeof node !== "object") return "";

  for (const key of keys) {
    const match = Object.keys(node).find((name) => name.toLowerCase() === key);
    const text = match ? textOf(node[match]) : "";
    if (text) return text;
  }

  for (const value of Object.values(node)) {
    if (value && typeof value === "object") {
      const nested = readNamedField(value, keys, depth + 1);
      if (nested) return nested;
    }
  }
  return "";
};

const readBilling = (body) => ({
  state: readNamedField(body, STATE_KEYS),
  city: readNamedField(body, CITY_KEYS),
});

const StoreOverview = ({
  packageData,
  selectedPackage,
  selectedPackageId,
  selectedFrequentPackages,
  addonDiscount,
  upgradeCredit = 0,
  upgradePayload = null,
  upgradeCheckDone = true,
  finalAmount,
  setFinalAmount,
  couponCode,
  setCouponCode,
  couponDiscount,
  setCouponDiscount,
  setAddonDiscount,
  startLoading,
  finishLoading,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.user_id);
  const profileAddress = useSelector((state) => state.auth.user?.address || {});
  const paymentDetails = useSelector((state) => state.paymentReducer.paytmDetails);
  const details = packageData?.PackageInstallmentDetails || {};
  const packageId = selectedPackage?.package_id || details.package_id || selectedPackageId;
  const [billing, setBilling] = useState({ state: "", city: "" });
  const [showCouponList, setShowCouponList] = useState(false);
  const [isCouponApplied, setIsCouponApplied] = useState(Number(couponDiscount) > 0);

  useEffect(() => {
    let cancelled = false;
    axioInstance
      .get(url_billing_address)
      .then((response) => {
        if (!cancelled) setBilling(readBilling(response?.data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const mrp = Number(packageData?.price_details?.course_price || details.amount || 0);
  const packageDiscount = Number(packageData?.price_details?.discount || 0);
  const couponValue = Number(couponDiscount || 0);
  const listedPayable = Math.max(0, mrp - packageDiscount - Number(upgradeCredit || 0));
  const payable = Number.isFinite(Number(finalAmount)) ? Number(finalAmount) : listedPayable;
  const creditValue = Math.max(0, Number(upgradeCredit || 0));
  const discounts = Math.max(0, mrp - payable - couponValue - creditValue);
  const saved = Math.max(0, mrp - payable);
  const percent = mrp > 0 ? Math.round((saved / mrp) * 100) : 0;
  const chips = productChips(details);
  const billingState = billing.state || textOf(profileAddress.state || profileAddress.state_name);
  const billingCity =
    billing.city ||
    textOf(
      profileAddress.city_name ||
        profileAddress.city ||
        profileAddress.district_name ||
        profileAddress.district
    );

  const handleCloseCoupon = useCallback(() => setShowCouponList(false), []);
  const handleOnChangeCouponCode = useCallback(
    (event) => setCouponCode(event.target.value.toUpperCase()),
    [setCouponCode]
  );
  const checkApplyCouponBtnDisabled = useCallback(
    () => (String(couponCode || "").trim().length === 0 ? "disableapplybtn" : ""),
    [couponCode]
  );

  const handleApplyCoupon = useCallback(() => {
    applyCoupon({
      couponCode: String(couponCode || "").trim(),
      userId,
      packageId,
      selectedPackage,
      packageData,
      setFinalAmount,
      setCouponDiscount,
      setAddonDiscount,
      startLoading,
      finishLoading,
      handleCloseCoupon,
      setIsCouponApplied,
      finalAmount,
      orderId: paymentDetails?.order_id || paymentDetails?.Order_Id || "",
    });
  }, [
    couponCode,
    userId,
    packageId,
    selectedPackage,
    packageData,
    setFinalAmount,
    setCouponDiscount,
    setAddonDiscount,
    startLoading,
    finishLoading,
    handleCloseCoupon,
    finalAmount,
    paymentDetails,
  ]);

  const handleRemoveCoupon = useCallback(() => {
    setCouponCode("");
    setCouponDiscount("0.00");
    setIsCouponApplied(false);
    dispatch(setOrderId(""));
    const basePrice = mrp - packageDiscount - Number(upgradeCredit || 0) - Number(addonDiscount || 0);
    setFinalAmount(Math.max(0, basePrice).toFixed(2));
  }, [
    setCouponCode,
    setCouponDiscount,
    setFinalAmount,
    dispatch,
    mrp,
    packageDiscount,
    upgradeCredit,
    addonDiscount,
  ]);

  const openCoupons = () => {
    trackWebEngageViewCoupons();
    setShowCouponList(true);
  };

  return (
    <div className="plan-overview">
      <header className="plan-overview__header">
        <button
          type="button"
          className="plan-overview__back"
          aria-label="Back"
          onClick={() => history.push(`/package/${packageId}`)}
        >
          <img src={backIcon} alt="" />
        </button>
        <h1>Overview</h1>
      </header>

      <div className="plan-overview__body">
        <div className="plan-overview__main">
          <section className="plan-overview__product">
            {chips.length ? (
              <div className="plan-overview__chips">
                {chips.map((chip) => (
                  <span key={chip}>{chip}</span>
                ))}
              </div>
            ) : null}
            <h2>{details.package_name || "Selected plan"}</h2>
            <div className="plan-overview__price">
              <strong>{formatRupee(payable)}</strong>
              {saved > 0 ? <s>{formatRupee(mrp)}</s> : null}
              {percent > 0 ? <em>{percent}% off</em> : null}
            </div>
          </section>

          <section className="plan-overview__billing">
            <div className="plan-overview__billing-head">
              <div>
                <h3>Billing Address *</h3>
                <p>Used for GST purposes</p>
              </div>
              <button type="button" onClick={() => history.push("/profile")}>
                Update
              </button>
            </div>
            <div className="plan-overview__billing-grid">
              <div>
                <span>State:</span>
                <strong>{billingState || "—"}</strong>
              </div>
              <div>
                <span>City:</span>
                <strong>{billingCity || "—"}</strong>
              </div>
            </div>
          </section>

          <section className="plan-overview__coupon">
            <div className="plan-overview__coupon-title">
              <span className="plan-overview__coupon-icon">
                <img src={couponIcon} alt="" />
              </span>
              <h3>Have a Coupon Code?</h3>
            </div>
            <div className="plan-overview__coupon-row">
              <input
                type="text"
                placeholder="Enter Coupon Code"
                value={couponCode}
                onChange={handleOnChangeCouponCode}
              />
              <button
                type="button"
                disabled={!isCouponApplied && !String(couponCode || "").trim()}
                onClick={isCouponApplied ? handleRemoveCoupon : handleApplyCoupon}
              >
                {isCouponApplied ? "Remove" : "Apply"}
              </button>
            </div>
            <button type="button" className="plan-overview__coupons" onClick={openCoupons}>
              See all Coupons
              <img src={chevronIcon} alt="" />
            </button>
            <ToastContainer
              enableMultiContainer
              containerId={STORE_PRICE_TOAST_CONTAINER_ID}
              autoClose={5000}
              hideProgressBar={false}
              closeOnClick
              pauseOnHover
              newestOnTop
              limit={1}
            />
          </section>
        </div>

        <aside className="plan-overview__cart">
          <h3>Cart Summary</h3>
          <div className="plan-overview__rows">
            <p>
              <span>MRP</span>
              <strong>{formatRupee(mrp)}</strong>
            </p>
            <p>
              <span>Discounts</span>
              <strong className="is-save">{`-${formatRupee(discounts)}`}</strong>
            </p>
            {creditValue > 0 ? (
              <p>
                <span>Upgrade Credit</span>
                <strong className="is-save">{`-${formatRupee(creditValue)}`}</strong>
              </p>
            ) : null}
            <p>
              <span>Delivery Charges</span>
              <strong>{formatRupee(0)}</strong>
            </p>
            <p>
              <span>Coupon Discount</span>
              <strong className="is-save">{`-${formatRupee(couponValue)}`}</strong>
            </p>
          </div>
          <img className="plan-overview__line" src={dividerIcon} alt="" />
          <p className="plan-overview__total">
            <span>Total Payable</span>
            <strong>{formatRupee(payable)}</strong>
          </p>
          <PaytmPayment
            addon_products={selectedFrequentPackages}
            packageDetails={{
              ...(packageData?.package_quick_variants?.show_quick_variants_selection_type === 2
                ? selectedPackage
                : details),
              package_id: packageId,
            }}
            amount={Math.max(0, payable).toFixed(2)}
            deltaUpgradePayload={upgradePayload}
            disabled={!upgradeCheckDone}
            couponCode={isCouponApplied ? couponCode : ""}
            buttonText="Proceed to pay →"
            buttonClass="plan-overview__pay"
          />
        </aside>
      </div>

      {showCouponList ? (
        <CouponList
          userId={userId}
          package_id={packageId}
          paymentDetails={paymentDetails}
          packageData={packageData}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          applyCoupon={handleApplyCoupon}
          handleCloseCoupon={handleCloseCoupon}
          checkApplyCouponBtnDisabled={checkApplyCouponBtnDisabled}
          handleOnChangeCouponCode={handleOnChangeCouponCode}
        />
      ) : null}
    </div>
  );
};

export default StoreOverview;
